import { apiFetch } from './prospects.js';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/v1/api';

function normalizeHubupApiOrigin(raw) {
  if (raw == null) return '';
  let s = String(raw).trim().replace(/\/+$/, '');
  if (!s) return '';
  s = s.replace(/\/v1\/api$/i, '');
  return s;
}

const HUBUP_MEDIA_ORIGIN_ENV = normalizeHubupApiOrigin(
  import.meta.env.VITE_HUBUP_API_ORIGIN ||
    import.meta.env.VITE_HUBUP_ASSETS_BASE ||
    '',
);

const HUBUP_PUBLIC_ORIGIN = import.meta.env.VITE_HUBUP_PUBLIC_ORIGIN || '';

let serverHubupApiOrigin = '';

function ingestHubupApiMeta(data) {
  const o = data?.hubupApiOrigin;
  if (o) serverHubupApiOrigin = normalizeHubupApiOrigin(o);
}

function hubupMediaOrigin() {
  return HUBUP_MEDIA_ORIGIN_ENV || serverHubupApiOrigin;
}

export function resolveHubupAssetUrl(url) {
  if (url == null || url === '') return '';
  const s = String(url).trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  const base = hubupMediaOrigin();
  if (!base) return s;
  const path = s.replace(/^\//, '');
  return `${base}/${path}`;
}

export function hubupGroupIconImgSrc(groupIconUrl, cacheBust) {
  const base = resolveHubupAssetUrl(groupIconUrl);
  if (!base) return '';
  if (/[?&]v=/.test(base)) return base;
  if (cacheBust == null) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}_cb=${cacheBust}`;
}

/** Favicon CRM (QR) — servi par l’API CRM, pas le BO HubUP. */
export function hubupCommunityFaviconImgSrc(faviconUrl, cacheBust) {
  const rel = String(faviconUrl ?? '').trim();
  if (!rel) return '';
  const base = /^https?:\/\//i.test(rel)
    ? rel
    : `${API_BASE_URL.replace(/\/+$/, '')}${rel.startsWith('/') ? rel : `/${rel}`}`;
  if (/[?&]v=/.test(base)) return base;
  if (cacheBust == null) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}_cb=${cacheBust}`;
}

export function buildHubupGroupInviteUrl(inviteCode) {
  const code = String(inviteCode ?? '').trim();
  if (!code) return '';
  const origin = HUBUP_PUBLIC_ORIGIN.replace(/\/$/, '');
  if (!origin) return '';
  return `${origin}/notification-feed?invite=${encodeURIComponent(code)}`;
}

export function buildHubupPublicProfileUrl(profileSlug) {
  const slug = String(profileSlug ?? '').trim();
  if (!slug) return '';
  const origin = HUBUP_PUBLIC_ORIGIN.replace(/\/$/, '');
  if (!origin) return '';
  return `${origin}/${encodeURIComponent(slug)}`;
}

/** Lien public vers une notification publiée dans l’app HubUP. */
export function buildHubupNotificationUrl(messageId) {
  const id = String(messageId ?? '').trim();
  if (!id) return '';
  return `https://app.hubup.info/notifications/${encodeURIComponent(id)}`;
}

/** Lien public vers le formulaire d'une notification HubUP. */
export function buildHubupFormUrl(messageId) {
  const id = String(messageId ?? '').trim();
  if (!id) return '';
  return `https://app.hubup.info/forms/${encodeURIComponent(id)}`;
}

/** Scrape une URL publique et renvoie le texte brut (sans HTML). */
export function scrapeHubupPageUrl(groupId, { url, language } = {}) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/scrape-page`,
    {
      method: 'POST',
      body: JSON.stringify({
        url: String(url ?? '').trim(),
        language: String(language ?? 'fr').trim() || 'fr',
      }),
    },
  );
}

/** Télécharge des URLs publiques et les envoie au BO HubUP (illustrations notif). */
export function importHubupNotificationMediaFromUrls(groupId, { urls, existingCount = 0 } = {}) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/import-notification-media`,
    {
      method: 'POST',
      body: JSON.stringify({
        urls: Array.isArray(urls) ? urls : [],
        existingCount: Number(existingCount) || 0,
      }),
    },
  );
}

export async function fetchHubupCommunities() {
  const res = await apiFetch('/hubup/communities');
  ingestHubupApiMeta(res?.data);
  return res;
}

export async function fetchMyHubupCommunities() {
  const res = await apiFetch('/hubup/my-communities');
  ingestHubupApiMeta(res?.data);
  return res;
}

export async function fetchHubupGroupTopics(groupId) {
  const res = await apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/topics`,
  );
  ingestHubupApiMeta(res?.data);
  return res;
}

/** QR premium (logo communauté, palette app) — généré côté serveur. */
export async function fetchHubupGroupInviteQr(groupId) {
  const res = await apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/invite-qr`,
  );
  ingestHubupApiMeta(res?.data);
  return res;
}

export async function fetchHubupGroup(groupId) {
  const res = await apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}`,
  );
  ingestHubupApiMeta(res?.data);
  return res;
}

export function patchHubupGroupJoinWelcome(groupId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/join-welcome`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export function patchHubupGroupAnnuary(groupId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/annuary`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export function patchHubupGroupStancerKeys(groupId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/stancer-keys`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export function patchHubupGroupFeedBrand(groupId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/feed-brand`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export function fetchHubupGroupCatchallSender(groupId) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/catchall-sender`,
  );
}

export function patchHubupGroupCatchallSender(groupId, senderEmail) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/catchall-sender`,
    {
      method: 'PATCH',
      body: JSON.stringify({ senderEmail }),
    },
  );
}

export function patchHubupGroupPublicProfile(groupId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/public-profile`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export function uploadHubupGroupCommunityIcon(groupId, file) {
  const fd = new FormData();
  fd.append('icon', file);
  return apiFetch(`/hubup/groups/${encodeURIComponent(groupId)}/icon`, {
    method: 'POST',
    body: fd,
  });
}

export function uploadHubupCommunityFavicon(groupId, file) {
  const fd = new FormData();
  fd.append('icon', file);
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/favicon`,
    {
      method: 'POST',
      body: fd,
    },
  );
}

export async function fetchHubupGroupAr(groupId) {
  return apiFetch(`/hubup/groups/${encodeURIComponent(groupId)}/ar`);
}

export function patchHubupGroupAr(groupId, payload) {
  return apiFetch(`/hubup/groups/${encodeURIComponent(groupId)}/ar`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteHubupGroupAr(groupId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/groups/${encodeURIComponent(groupId)}/ar`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) return { success: true };
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

/** QR → cible + .mind côté serveur, puis asset affiché (image/vidéo). */
export async function publishHubupGroupAr(groupId, { file, url } = {}) {
  const fd = new FormData();
  if (file) fd.append('file', file);
  if (url !== undefined) fd.append('url', String(url ?? ''));
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/ar/publish`,
    {
      method: 'POST',
      body: fd,
    },
  );
}

/** Aperçu favicon CRM (évite les soucis d’URL / cache sur balise img). */
export async function fetchHubupCommunityFaviconBlobUrl(groupId, cacheBust) {
  const gid = String(groupId ?? '').trim();
  if (!gid) return null;
  const token = localStorage.getItem('authToken');
  const sep = cacheBust != null ? `?v=${encodeURIComponent(String(cacheBust))}` : '';
  const res = await fetch(
    `${API_BASE_URL}/hubup/groups/${encodeURIComponent(gid)}/favicon${sep}`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!res.ok) return null;
  const blob = await res.blob();
  if (!blob.size || !String(blob.type || '').startsWith('image/')) {
    return null;
  }
  return URL.createObjectURL(blob);
}

export async function deleteHubupCommunityFavicon(groupId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/groups/${encodeURIComponent(groupId)}/favicon`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) {
    return { success: true };
  }
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

export async function deleteHubupGroupCommunityIcon(groupId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/groups/${encodeURIComponent(groupId)}/icon`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) {
    return { success: true };
  }
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

export function fetchHubupPublishApprovals() {
  return apiFetch('/hubup/admin/publish-approvals');
}

export function updateHubupPublishApproval(publicationId, payload) {
  return apiFetch(
    `/hubup/admin/publish-approvals/${encodeURIComponent(publicationId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteHubupPublishApproval(publicationId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/admin/publish-approvals/${encodeURIComponent(publicationId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    let message = `API error: ${res.status}`;
    try {
      const data = await res.json();
      message = data?.data?.message || message;
    } catch {
    }
    const err = new Error(message);
    throw err;
  }
  if (res.status === 204) {
    return { success: true };
  }
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

export async function fetchHubupAnnuary() {
  const res = await fetch(`${API_BASE_URL}/hubup/group/annuary`, {
    headers: { Accept: 'application/json' },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.data?.message || 'Annuaire indisponible');
    err.data = data;
    throw err;
  }
  return data;
}

export function createHubupGroupTopic(groupId, payload) {
  return apiFetch(`/hubup/groups/${encodeURIComponent(groupId)}/topics`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateHubupTopic(topicId, payload) {
  return apiFetch(`/hubup/admin/topics/${encodeURIComponent(topicId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteHubupTopic(topicId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/admin/topics/${encodeURIComponent(topicId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) {
    return { success: true };
  }
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

export async function fetchHubupGroupMessages(groupId) {
  const res = await apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/messages`,
  );
  ingestHubupApiMeta(res?.data);
  return res;
}

export async function fetchHubupGroupScheduledNotifications(groupId) {
  const res = await apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/scheduled-notifications`,
  );
  ingestHubupApiMeta(res?.data);
  return res;
}

export function patchHubupGroupScheduledNotification(groupId, scheduleId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/scheduled-notifications/${encodeURIComponent(scheduleId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteHubupGroupScheduledNotification(groupId, scheduleId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/groups/${encodeURIComponent(groupId)}/scheduled-notifications/${encodeURIComponent(scheduleId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) {
    return { success: true };
  }
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

export async function fetchHubupGroupStats(groupId) {
  const res = await apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/stats`,
  );
  ingestHubupApiMeta(res?.data);
  return res;
}

export async function fetchHubupGroupNotificationsHourly(
  groupId,
  { start, end } = {},
) {
  const qs = new URLSearchParams();
  if (start) qs.set('start', start);
  if (end) qs.set('end', end);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notifications-hourly${suffix}`,
  );
  ingestHubupApiMeta(res?.data);
  return res;
}

export function createHubupTopicMessage(groupId, topicId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/topics/${encodeURIComponent(topicId)}/messages`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export function createHubupNotificationAllTopics(payload) {
  return apiFetch('/hubup/admin/notifications', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateHubupMessage(messageId, payload) {
  return apiFetch(`/hubup/admin/messages/${encodeURIComponent(messageId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function fetchHubupMessageForm(messageId) {
  return apiFetch(`/hubup/admin/messages/${encodeURIComponent(messageId)}/form`);
}

export function fetchHubupMessageCalendarEvent(messageId) {
  return apiFetch(
    `/hubup/admin/messages/${encodeURIComponent(messageId)}/calendar-event`,
  );
}

export function fetchHubupMessageFormResponses(groupId, messageId) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/messages/${encodeURIComponent(messageId)}/form-responses`,
  );
}

export function fetchHubupGroupFormAnnuaire(groupId) {
  return apiFetch(`/hubup/groups/${encodeURIComponent(groupId)}/form-annuaire`);
}

export function syncHubupGroupFormAnnuaire(groupId) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/form-annuaire/sync`,
    { method: 'POST' },
  );
}

export async function deleteHubupMessage(messageId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/admin/messages/${encodeURIComponent(messageId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) {
    return { success: true };
  }
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

export function resendHubupMessagePush(messageId, groupId) {
  const q =
    groupId != null && String(groupId).trim()
      ? `?groupId=${encodeURIComponent(String(groupId))}`
      : '';
  return apiFetch(
    `/hubup/admin/messages/${encodeURIComponent(messageId)}/resend-push${q}`,
    { method: 'POST' },
  );
}

export function uploadHubupNotificationImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  return apiFetch('/hubup/admin/notification-image', {
    method: 'POST',
    body: fd,
  });
}

export function createHubupAdminGroup(payload) {
  const body =
    typeof payload === 'string' ? { name: payload } : payload;
  return apiFetch('/hubup/admin/groups', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateHubupAdminGroup(id, payload) {
  const body =
    typeof payload === 'string'
      ? { name: payload }
      : payload;
  return apiFetch(`/hubup/admin/groups/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteHubupAdminGroup(id) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/admin/groups/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (res.ok) {
    if (res.status === 204) {
      return { success: true };
    }
    try {
      return await res.json();
    } catch {
      return { success: true };
    }
  }
  let message = `Erreur ${res.status}`;
  try {
    const body = await res.json();
    message =
      body?.data?.message ||
      body?.message ||
      body?.error ||
      message;
  } catch {
    /* corps vide ou non JSON */
  }
  const err = new Error(message);
  err.status = res.status;
  throw err;
}

export function uploadHubupGroupIcon(id, file) {
  const fd = new FormData();
  fd.append('icon', file);
  return apiFetch(`/hubup/admin/groups/${encodeURIComponent(id)}/icon`, {
    method: 'POST',
    body: fd,
  });
}

export async function deleteHubupGroupIcon(id) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/admin/groups/${encodeURIComponent(id)}/icon`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) {
    return { success: true };
  }
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

export async function fetchHubupNotificationDrafts(groupId) {
  const res = await apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notification-drafts`,
  );
  ingestHubupApiMeta(res?.data);
  return res;
}

export function createHubupNotificationDraft(groupId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notification-drafts`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export function updateHubupNotificationDraft(groupId, draftId, payload) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notification-drafts/${encodeURIComponent(draftId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteHubupNotificationDraft(groupId, draftId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/hubup/groups/${encodeURIComponent(groupId)}/notification-drafts/${encodeURIComponent(draftId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) {
    return { success: true };
  }
  try {
    return await res.json();
  } catch {
    return { success: true };
  }
}

export function publishHubupNotificationDraft(groupId, draftId) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notification-drafts/${encodeURIComponent(draftId)}/publish`,
    { method: 'POST' },
  );
}

export function fetchHubupNotificationPackOffers(groupId) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notification-pack-offers`,
  );
}

export function createHubupNotificationPackCheckout(groupId, { packId, returnUrl, affiliateCode }) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notification-pack/checkout`,
    {
      method: 'POST',
      body: JSON.stringify({ packId, returnUrl, affiliateCode: affiliateCode || undefined }),
    },
  );
}

export function quoteHubupNotificationPack(groupId, { packId, affiliateCode }) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notification-pack/quote`,
    {
      method: 'POST',
      body: JSON.stringify({ packId, affiliateCode: affiliateCode || undefined }),
    },
  );
}

export function confirmHubupNotificationPackPayment(groupId, paymentId) {
  return apiFetch(
    `/hubup/groups/${encodeURIComponent(groupId)}/notification-pack/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({ paymentId }),
    },
  );
}
